/**
 * Avisos de "Atualizações / Melhorias recentes" do ContaCil.
 *
 * O aviso representa o PRODUTO, não um repositório. Uma melhoria pode nascer na
 * leitura de extratos, passar pelo servidor e aparecer na tela — para o contador
 * é UMA correção, e vira UM aviso.
 *
 * REGRAS DE ESCRITA (o card é lido por contador, não por quem programa):
 * - CURTO. Uma ou duas frases. Quem lê está no meio do trabalho, não veio ler
 *   release note. Aviso comprido não é lido até o fim, então a parte que
 *   importa tem que vir na primeira frase;
 * - sem ressalva, sem explicação de causa, sem "em alguns casos", sem contar
 *   como o defeito acontecia. Diz o que mudou, não por quê;
 * - sem promessa em nome do time ("avisaremos", "entre em contato", "em breve").
 *   O card informa o que mudou; não assume compromisso de prazo nem de canal;
 * - nada de nome de função, arquivo, tabela, parser, banco de dados, hash,
 *   número de PR, branch ou serviço interno;
 * - nada de nome de cliente, CNPJ, conta, valor real ou quantidade de empresas
 *   afetadas.
 *
 * REGRA DO `id`: slug estável, escrito à mão, no formato `<tela>-<assunto>`.
 * NUNCA derivar de número de PR ou hash — parte das melhorias sobe por caminho
 * que não tem número de PR, e hash muda a cada rebase. Uma vez publicado, o id é
 * IMUTÁVEL: ele é a chave de "já vi este aviso". Corrigir texto mantém o id;
 * mudança de assunto pede id novo.
 *
 * REGRA DO `emProducao`: nasce `false`, no MESMO commit que implementa a
 * melhoria. Virar para `true` é um commit separado, de uma linha, depois de a
 * mudança estar no ar. É isso que impede o card de prometer o que ainda não
 * existe para o usuário — e vale também para o que foi revertido depois de
 * publicado: nesse caso volta para `false`.
 */
export interface Atualizacao {
  id: string;
  titulo: string;
  texto: string;
  /**
   * AAAA-MM-DD em que o aviso entra no card. Ordena a lista e decide a poda.
   *
   * Normalmente é o mesmo dia em que a melhoria chegou ao usuário, porque o
   * aviso acende junto. Quando uma correção antiga só é comunicada depois — foi
   * ao ar sem aviso e apareceu numa revisão posterior — vale a data da
   * PUBLICAÇÃO, e a data real da correção fica no `interno.evidencia`. Datar
   * para trás enterraria o aviso sob os recentes e ele nasceria podado, isto é,
   * não seria comunicado de novo.
   */
  data: string;
  /** Telas em que a melhoria aparece. Só informativo no card. */
  areas: string[];
  /** Só entra na tela quando true. Ver regra acima. */
  emProducao: boolean;
  /**
   * Rastreabilidade interna — NUNCA renderizado. Existe para responder, daqui a
   * seis meses, de onde veio a mudança e se ela foi medida ou só anunciada.
   */
  interno: { origem: string; evidencia?: string };
}

/**
 * Teto de avisos exibidos. Ao passar disso, o mais ANTIGO sai — o card é sobre o
 * que mudou agora, não um histórico. Manter a lista curta também é o que faz o
 * carrossel ser percorrível: com trinta avisos ninguém chega ao fim.
 * Aviso podado não vira dívida: some da tela e pode ser apagado do arquivo na
 * próxima faxina.
 */
export const LIMITE_AVISOS = 10;

/**
 * Mais recentes primeiro — e a ordem DESTE array é o desempate.
 *
 * `data` tem precisão de dia, então vários avisos empatam no mesmo dia e a
 * ordenação preserva a ordem em que estão escritos aqui. Por isso:
 *
 *   >>> aviso que acabou de entrar no ar vai para o TOPO da lista, <<<
 *   >>> não para o fim.                                            <<<
 *
 * Quando um aviso de "aguardando" acender, mova o bloco para cá em cima, junto
 * dos publicados. Deixar no fim faz a novidade nascer enterrada sob avisos
 * antigos do mesmo dia — que foi exatamente o que aconteceu antes.
 */
export const ATUALIZACOES: Atualizacao[] = [
  {
    id: 'exportar-historico-com-nota-fiscal',
    titulo: 'Arquivo do Domínio traz a nota fiscal',
    texto:
      'O histórico do arquivo gerado em Exportar passou a trazer o número da nota fiscal, com o nome e o documento do fornecedor ou cliente, nos lançamentos com nota identificada.',
    data: '2026-10-05',
    areas: ['Exportar'],
    emProducao: false,
    interno: {
      origem: 'backend',
      evidencia:
        'medição em uma empresa: 273 de 5.522 conciliados com nota confirmada por CNPJ ou nome; outros 1.904 tinham sugestão só por valor e data e ficam sem nota no arquivo',
    },
  },
  {
    id: 'importar-extrato-por-email',
    titulo: 'Novos extratos chegam por e-mail',
    texto:
      'A importação manual de extratos foi desabilitada. Para novas importações, envie o extrato pelo e-mail definido para o processo automático.',
    data: '2026-09-11',
    areas: ['Importar'],
    emProducao: false,
    interno: {
      origem: 'admin',
      evidencia: 'upload oculto por IMPORTACAO_MANUAL_HABILITADA em ImportarExtratoContent',
    },
  },
  {
    id: 'exportar-historico-com-contraparte',
    titulo: 'Arquivo do Domínio identifica a contraparte',
    texto:
      'O histórico do arquivo gerado em Exportar passou a trazer o nome e o documento da contraparte de cada lançamento.',
    data: '2026-09-02',
    areas: ['Exportar'],
    emProducao: false,
    interno: {
      origem: 'backend',
      evidencia:
        'medição na base: 4.047 de 51.176 lançamentos conciliados exportáveis saíam só com o histórico truncado do extrato; 33,6% dos conciliados do Itaú',
    },
  },
  {
    id: 'filtro-banco-extratos-exportar',
    titulo: 'Filtro de banco volta a encontrar os documentos',
    texto:
      'Escolher um banco em Extratos, Conciliar e Exportar passou a trazer os documentos daquele banco.',
    data: '2026-08-19',
    areas: ['Extratos', 'Conciliar', 'Exportar'],
    emProducao: true,
    interno: {
      origem: 'backend',
      evidencia:
        'medição na base: 44,5% dos lançamentos e 48,4% dos extratos eram inalcançáveis pelo rótulo da tela',
    },
  },
  {
    id: 'exportar-filtro-por-conta',
    titulo: 'Exportar filtra por conta',
    texto:
      'O Exportar passou a filtrar por conta, além do banco, e aceita escolher mais de um de cada vez.',
    data: '2026-08-19',
    areas: ['Exportar'],
    emProducao: true,
    interno: { origem: 'backend+admin' },
  },
  {
    id: 'importar-arquivo-repetido-identificado',
    titulo: 'Arquivo repetido mostra qual extrato já existe',
    texto:
      'Ao vincular a empresa a um arquivo que já havia sido importado, a mensagem passou a identificar o extrato que já existe.',
    data: '2026-08-19',
    areas: ['Importar'],
    emProducao: true,
    interno: { origem: 'backend' },
  },
  {
    id: 'extrato-sem-lancamento-nao-libera',
    titulo: 'Extrato sem lançamento não é liberado',
    texto:
      'Extrato em que nenhum lançamento foi lido só pode ser liberado quando os saldos confirmam que o período não teve movimento. Reprocesse o documento.',
    data: '2026-08-19',
    areas: ['Extratos'],
    emProducao: true,
    interno: {
      origem: 'backend',
      evidencia: 'cinco extratos concluídos sem lançamento; dois com variação real de saldo',
    },
  },
  {
    id: 'extrato-segunda-secao-ultimos-dias',
    titulo: 'Extratos em duas partes lidos por inteiro',
    texto:
      'Extratos divididos em duas partes passaram a ser lidos por inteiro. Antes, os lançamentos dos últimos dias do período podiam ficar de fora. Vale para as próximas importações; não reimporte os extratos anteriores por enquanto.',
    data: '2026-08-14',
    areas: ['Importar', 'Extratos', 'Conciliar'],
    emProducao: true,
    interno: { origem: 'etl', evidencia: 'medição pareada no corpus de teste' },
  },
  {
    id: 'importar-abre-extrato-na-lista',
    titulo: 'Do Importar direto para o extrato',
    texto:
      'Ao clicar em um documento na tela de Importar, a lista de Extratos abre já posicionada nele, com o card destacado.',
    data: '2026-08-14',
    areas: ['Importar', 'Extratos'],
    emProducao: true,
    interno: { origem: 'admin' },
  },
  {
    // Aviso AGRUPADOR das correções de leitura do extrato. Todas têm a mesma
    // consequência para quem usa, então viram um aviso só — separar em cinco
    // faria cada uma parecer um caso isolado em vez de "confira seus extratos".
    //
    // O id mudou (era `extrato-saldo-negativo`, estreito demais para o que o
    // aviso passou a cobrir). Foi seguro porque este aviso NUNCA chegou à tela:
    // ficou `false` na versão publicada e o deploy que o acenderia falhou antes
    // de ir ao ar. Ninguém o dispensou, então ninguém o vê duas vezes.
    id: 'extrato-leitura-valores-e-datas',
    titulo: 'Valores e datas do extrato conforme o documento',
    texto:
      'Várias correções na leitura dos extratos: sinal do saldo em contas negativas, entrada e saída conforme o documento, lançamentos de valor alto, taxas que aparecem fora da movimentação e datas dos lançamentos. Vale para as próximas importações; não reimporte os extratos anteriores por enquanto.',
    data: '2026-08-14',
    areas: ['Extratos', 'Conciliar'],
    emProducao: true,
    interno: { origem: 'etl', evidencia: 'medição pareada no corpus de teste' },
  },
  {
    // Corrigido em 05/08 e nunca comunicado. Ver a nota sobre `data` acima: o
    // campo carrega a data de publicação, a real fica no `interno`.
    id: 'extrato-retido-aparece-nos-filtros',
    titulo: 'Extrato retido volta a aparecer nos filtros',
    texto:
      'Documentos retidos para validação, como fatura de cartão e extrato de aplicação, voltaram a aparecer no filtro por empresa e na busca por CNPJ ou titular.',
    data: '2026-08-14',
    areas: ['Importar', 'Extratos'],
    emProducao: true,
    interno: { origem: 'backend', evidencia: 'corrigido em 2026-08-05' },
  },
  {
    id: 'contrapartida-conclui-lancamento',
    titulo: 'Contrapartida escolhida conclui o lançamento',
    texto:
      'Ao escolher a contrapartida, o lançamento passa a ser concluído em vez de voltar para pendente.',
    data: '2026-08-14',
    areas: ['Conciliar'],
    emProducao: true,
    interno: { origem: 'backend' },
  },
  {
    id: 'lista-de-contas-abre-em-tela-menor',
    titulo: 'Lista de contas abre em telas de menor altura',
    texto:
      'A lista para escolher a contrapartida passa a abrir normalmente em telas de menor altura, inclusive para cima quando não há espaço abaixo do campo.',
    data: '2026-08-14',
    areas: ['Conciliar', 'Extratos', 'Bancos', 'Regras'],
    emProducao: true,
    interno: { origem: 'admin' },
  },
  {
    // Explicação de como o card funciona. Fica por último entre os do dia: já
    // não é novidade, mas ainda serve para quem abre o card pela primeira vez.
    id: 'card-melhorias-estreia',
    titulo: 'Como acompanhar as melhorias do módulo',
    texto:
      'As correções e melhorias da Conciliação Contábil aparecem aqui. Use as setas para percorrer e o X para recolher — o card vira um ícone abaixo do seu perfil e fica destacado quando há novidade.',
    data: '2026-08-14',
    areas: ['Conciliação Contábil'],
    emProducao: true,
    interno: { origem: 'admin' },
  },
  {
    id: 'bb-historico-completo',
    titulo: 'Extratos do Banco do Brasil: histórico completo',
    texto:
      'A descrição dos lançamentos voltou a vir inteira, com o nome de quem pagou ou recebeu e a hora do movimento. Vale para as próximas importações; não reimporte os extratos anteriores por enquanto.',
    data: '2026-08-13',
    areas: ['Extratos', 'Conciliar'],
    emProducao: true,
    interno: { origem: 'etl', evidencia: 'medição pareada no corpus de teste' },
  },
  {
    id: 'auto-conciliar-respeita-filtro',
    titulo: 'Auto-conciliar respeita o período filtrado',
    texto:
      'A conciliação automática passa a considerar apenas os lançamentos do recorte que está na tela — ano, mês, banco e conta.',
    data: '2026-08-11',
    areas: ['Conciliar'],
    emProducao: true,
    interno: { origem: 'backend + admin' },
  },
  {
    id: 'extrato-sem-empresa-tem-saida',
    titulo: 'Extrato sem empresa vinculada deixa de travar',
    texto:
      'Quando a empresa não é identificada automaticamente, a tela passa a oferecer a escolha da empresa em qualquer situação.',
    data: '2026-08-11',
    areas: ['Extratos'],
    emProducao: true,
    interno: { origem: 'backend + admin' },
  },
  {
    id: 'extrato-reprocessa-com-leitura-de-imagem',
    titulo: 'Extrato digitalizado é relido automaticamente',
    texto:
      'Documento que é lido sem nenhum lançamento passa a ser relido como imagem, o que recupera extratos digitalizados.',
    data: '2026-08-11',
    areas: ['Importar', 'Extratos'],
    emProducao: true,
    interno: { origem: 'etl' },
  },
  {
    id: 'extrato-conta-corrente-nao-vira-investimento',
    titulo: 'Conta corrente deixa de ser lida como investimento',
    texto:
      'Extratos de conta corrente que eram classificados como aplicação e chegavam sem lançamentos voltam completos.',
    data: '2026-08-11',
    areas: ['Importar', 'Extratos'],
    emProducao: true,
    interno: { origem: 'etl' },
  },

  // ── Prontos, aguardando entrar no ar. Viram `true` em commit próprio, e no
  //    mesmo commit sobem para o topo da lista acima. ────────────────────────
  {
    id: 'conciliar-filtros-a-resolver',
    titulo: 'Filtros "A resolver" com contagem',
    texto:
      'Os atalhos "Sem contrapartida", "Baixa confiança" e "Só IA" passam a mostrar quantos lançamentos existem em cada um.',
    data: '2026-08-14',
    areas: ['Conciliar'],
    emProducao: false,
    interno: { origem: 'backend + admin' },
  },
  {
    id: 'conciliar-indicadores-em-reais',
    titulo: 'Indicadores do topo em reais',
    texto:
      'Os indicadores de Composição e Líquido passam a exibir os valores em reais, abreviados quando são grandes. Ao passar o ponteiro, o valor aparece por extenso.',
    data: '2026-08-14',
    areas: ['Conciliar'],
    emProducao: false,
    interno: { origem: 'admin' },
  },
  {
    id: 'extrato-processamento-travado',
    titulo: 'Aviso quando a leitura do extrato não termina',
    texto:
      'Quando um extrato fica muito tempo em processamento, o card passa a avisar há quanto tempo está parado.',
    data: '2026-08-14',
    areas: ['Extratos'],
    emProducao: false,
    interno: { origem: 'backend + admin' },
  },
  {
    id: 'extrato-reenvio-mantem-repetidos',
    titulo: 'Reenvio de extrato mantém movimentos repetidos',
    texto:
      'Ao reenviar um extrato, movimentos que se repetem no mesmo dia com o mesmo valor passam a ser mantidos um a um.',
    data: '2026-08-14',
    areas: ['Importar', 'Extratos', 'Conciliar'],
    emProducao: false,
    interno: { origem: 'backend' },
  },
];

/**
 * PREVIEW LOCAL — mostra TODOS os avisos, inclusive os que ainda não estão no ar,
 * para avaliar visual e texto antes de subir.
 *
 * >>> TEM QUE ESTAR `false` EM QUALQUER COMMIT. <<<
 */
export const PREVIEW_LOCAL = false;

/** O que o card exibe: no ar, mais recentes primeiro, no máximo LIMITE_AVISOS. */
export const avisosVisiveis = (): Atualizacao[] =>
  [...ATUALIZACOES]
    .filter(a => PREVIEW_LOCAL || a.emProducao)
    .sort((a, b) => b.data.localeCompare(a.data))
    .slice(0, LIMITE_AVISOS);

/** Chave única no localStorage: ids já dispensados pelo usuário. */
export const STORAGE_AVISOS_VISTOS = 'contacil_avisos_vistos';

/**
 * Tempo de leitura do aviso, proporcional ao próprio texto: ~90ms por caractere
 * (leitura tranquila em tela), com piso de 8s para o aviso curto não piscar e
 * teto de 30s para o longo não ficar plantado. O usuário sempre pode fechar antes.
 */
export const tempoLeitura = (texto: string): number =>
  Math.min(30000, Math.max(8000, texto.length * 90));
